import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import HomeSidebar, { sidebarRoutes } from "../../components/NavigationAndStructure/HomeSidebar/HomeSidebar";
import { TopBar } from "../../components/NavigationAndStructure/TopBar";
import ErrorBoundaryPage from "../../components/ErrorBoundaryPage/ErrorBoundaryPage";
import { useGlobalContext, useStore } from "../../contexts";
import { routes } from "../../constants";
import InAppNotificationDrawer from "../../components/InAppNotificationsDrawer/InAppNotificationsDrawer";
import { LoginErrorModal } from "../../components/SecureLoginModal/LoginErrorModal";
import styles from "./HomePage.module.scss";
import { selectors } from "../../store/state-selectors";
import AgreementsDashboard from "../AgreementsDashboard/AgreementsDashboard";
import { ContactsDashboard, importContactsRoute } from "../ContactsDashboard";
import { DraftsDashboard } from "../DraftsDashboard";
import { Integrations } from "../../components/Integrations/Integrations";
import InvoicesDashboard from "../InvoicesDashboard/InvoicesDashboard";
import PayoutsDashboard from "../PayoutsDashboard/PayoutsDashboard";
import { ProposalsDashboard } from "../ProposalsDashboard";
import { importServicesRoute, ServiceLibrary } from "../ServiceLibrary/ServiceLibrary";
import VendorDashboard from "../VendorDashboard/VendorDashboard";
import ImportServices from "../ImportServices/ImportServices";
import ImportContacts from "../ImportContacts/ImportContacts";
import AgreementMinisite from "../../components/AgreementMinisite/AgreementMinisite";
import ProfilePage from "../Profile/Profile";
import { BusinessDetails } from "../BusinessDetails";
import ProposalDefaults from "../ProposalDefaults/ProposalDefaults";
import TeamPage from "../TeamPage/TeamPage";
import AnchorBilling from "../AnchorBilling/AnchorBilling";
import ImportAgreements from "../ImportAgreements/ImportAgreements";
import GlobalBillingHub from "../GlobalBillingHub/GlobalBillingHub";
import VendorOnboardingModal from "../../components/VendorOnboardingModal/VendorOnboardingModal";
import { GoogleMapsProvider, loadGoogleMaps } from "../../components/AddressAutocomplete";
import FullPageInvoiceOpener from "../../components/FullPageInvoice/FullPageInvoiceOpener";
import HomePageProvider, { useHomePage } from "./HomePageProvider";
import ReferralDashboard from "../ReferralDashboard/ReferralDashboard";
import { ProposalTemplates } from "../ProposalTemplates/ProposalTemplates";
import cn from 'classnames';
import { useQboInVob } from "../../components/VendorOnboardingModal/useQboInVob";
import { VobQboState } from "../../types/api.model";
import { userMenuRoutes } from "../../components/NavigationAndStructure/UserMenu/UserMenu";
import InAppNotificationBinder from "../../components/InAppNotificationsBinder";
import { withBlockPartiallyOnboardedVendor } from "../../components/withBlockPartiallyOnboardedVendor/withBlockPartiallyOnboardedVendor";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentMountMap = [{
  url: routes.vendorDashboard,
  component: VendorDashboard
}, {
  url: routes.agreementsDashboard,
  component: AgreementsDashboard
}, {
  url: routes.proposalsDashboard,
  component: ProposalsDashboard
}, {
  url: routes.draftsDashboard,
  component: DraftsDashboard
}, {
  url: routes.proposalTemplates,
  component: ProposalTemplates
}, {
  url: routes.proposalDefaults,
  component: ProposalDefaults
}, {
  url: routes.invoicesDashboard,
  component: InvoicesDashboard
}, {
  url: routes.payoutsDashboard,
  component: PayoutsDashboard
}, {
  url: routes.contacts,
  component: ContactsDashboard
}, {
  url: routes.serviceLibrary,
  component: ServiceLibrary
}, {
  url: routes.integrations,
  component: Integrations
}, {
  url: routes.profile,
  component: ProfilePage
}, {
  url: routes.businessDetails,
  component: BusinessDetails
}, {
  url: routes.team,
  component: TeamPage
}, {
  url: routes.anchorBilling,
  component: AnchorBilling
}, {
  url: routes.importAgreements,
  component: ImportAgreements
}, {
  url: routes.importServices,
  component: ImportServices
}, {
  url: routes.importContacts,
  component: ImportContacts
}, {
  url: routes.minisite(undefined),
  component: AgreementMinisite
}, {
  url: routes.globalBillingHub,
  component: GlobalBillingHub
}, {
  url: routes.referrals,
  component: ReferralDashboard
}].reduce((map, _ref) => {
  let {
    url,
    component
  } = _ref;
  map[url] = withBlockPartiallyOnboardedVendor(component);
  return map;
}, {});
const appRoutes = [{
  id: 'minisite',
  text: '',
  url: routes.minisite(undefined),
  shouldShow: true
}];
export function ActualHomePage(props) {
  var _worksAt$features;
  const globalContext = useGlobalContext();
  const history = useHistory();
  const userInfo = useStore(state => state.userInfo);
  const {
    hidden
  } = useHomePage();
  const isVendor = useStore(selectors.user.isVendor);
  const pageLinks = useStore(selectors.filterAvailableRoutes(sidebarRoutes.concat(userMenuRoutes).concat(importContactsRoute).concat(importServicesRoute).concat(appRoutes)));
  const {
    firstName,
    lastName,
    worksAt
  } = userInfo || {};
  const isClient = !!(worksAt !== null && worksAt !== void 0 && (_worksAt$features = worksAt.features) !== null && _worksAt$features !== void 0 && _worksAt$features.accountsPayable);
  const isAnchorAdmin = useStore(selectors.user.isAnchorAdmin);
  const {
    getState: getVobQboState
  } = useQboInVob();
  const isLinkedToQbo = useStore(selectors.integration.isLinkedToQbo);
  const openVOB = async vobQboState => {
    return globalContext.showModal({
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(GoogleMapsProvider, {
          loadGoogleMaps: loadGoogleMaps
        }, /*#__PURE__*/React.createElement(VendorOnboardingModal, {
          dismissModal: dismissModal,
          vobQboState: vobQboState
        }));
      },
      closeOnClickOutside: false,
      shouldBlurBackground: true
    });
  };
  const hasBusiness = !!(worksAt && worksAt.name && worksAt.hasStripeAccount && worksAt.businessTypes.length);
  useEffect(() => {
    const hasName = !!firstName && !!lastName;
    const vobDone = hasName && hasBusiness;
    getVobQboState().then(vobQboState => {
      const startedQboFlow = vobQboState === VobQboState.Started;
      const pendingQboSetup = vobQboState === VobQboState.ConnectionTriggered && isLinkedToQbo;
      if (!isClient && (!vobDone || startedQboFlow || pendingQboSetup)) {
        history.push(routes.vendorDashboard);
        openVOB(vobQboState).then(async finishedVOB => {
          if (!finishedVOB) openVOB(vobQboState);
        });
      }
    });
  }, [hasBusiness, isClient]);
  function getAllNavigation(items) {
    return items.flatMap(item => 'url' in item ? item : 'items' in item ? getAllNavigation(item.items) : []);
  }
  return hidden ? null : /*#__PURE__*/React.createElement(FullPageInvoiceOpener, null, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.mainContainer, {
      [styles.anchorAdmin]: isAnchorAdmin
    }),
    "data-testid": "home-screen"
  }, /*#__PURE__*/React.createElement(InAppNotificationDrawer, null), /*#__PURE__*/React.createElement(HomeSidebar, null), /*#__PURE__*/React.createElement("div", {
    className: styles.mainContent
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.mainContentWithTopBar
  }, /*#__PURE__*/React.createElement(TopBar, {
    "data-testid": "topbar-home"
  }), /*#__PURE__*/React.createElement(ErrorBoundaryPage, {
    bounded: true
  }, /*#__PURE__*/React.createElement(Switch, null, props.children, getAllNavigation(pageLinks).map(_ref3 => {
    let {
      url,
      exact
    } = _ref3;
    return /*#__PURE__*/React.createElement(Route, {
      key: url,
      path: url,
      exact: exact,
      component: componentMountMap[url]
    });
  }), /*#__PURE__*/React.createElement(Redirect, {
    to: routes.defaultAuthorizedRoute(isVendor)
  }))), /*#__PURE__*/React.createElement(Route, {
    path: "*".concat(routes.loginError),
    render: () => /*#__PURE__*/React.createElement(LoginErrorModal, null)
  })))));
}
export default function HomePage(props) {
  return /*#__PURE__*/React.createElement(HomePageProvider, null, /*#__PURE__*/React.createElement(InAppNotificationBinder, null), /*#__PURE__*/React.createElement(ActualHomePage, props));
}